import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "./Authentication/AuthContext";
import { fetchUserDashboard } from "../apis/DashboardApis";
import {
  ChartCard,
  SimpleAreaChart,
  DonutChart,
  ProgressRing,
  CHART_COLORS,
  GRADIENT_COLORS,
} from "./charts/ChartComponents";
import { SkeletonDashboard } from "./Skeletons";
import SeoMeta from "./SeoMeta";
import { useTheme } from "./Theme/ThemeContext";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const cardHover = {
  rest: { scale: 1 },
  hover: { scale: 1.02, transition: { duration: 0.2 } },
};

// Default achievements structure (used as fallback)
const DEFAULT_ACHIEVEMENTS = [
  { id: "streak_7", icon: "ri-fire-line", label: "7 Day Streak", color: "from-orange-500 to-red-500", unlocked: false },
  { id: "first_code", icon: "ri-code-s-slash-line", label: "First Code", color: "from-green-500 to-emerald-500", unlocked: false },
  { id: "course_complete", icon: "ri-trophy-line", label: "Course Complete", color: "from-yellow-500 to-amber-500", unlocked: false },
  { id: "fast_learner", icon: "ri-rocket-line", label: "Fast Learner", color: "from-blue-500 to-cyan-500", unlocked: false },
  { id: "perfect_score", icon: "ri-star-line", label: "Perfect Score", color: "from-purple-500 to-pink-500", unlocked: false },
  { id: "champion", icon: "ri-medal-line", label: "Champion", color: "from-indigo-500 to-violet-500", unlocked: false },
];

export default function UserDashboard() {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.token) return;

      setLoading(true);
      setError(null);

      try {
        // Single consolidated API call
        const response = await fetchUserDashboard(user.token);
        
        if (response?.success && response?.data) {
          setDashboardData(response.data);
        } else if (response?.data) {
          // Handle case where response is directly the data
          setDashboardData(response.data);
        } else {
          // Handle legacy response format
          setDashboardData(response);
        }
      } catch (err) {
        console.error("Error loading dashboard:", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.token]);

  // Extract data from consolidated response
  const userDetails = dashboardData?.user || null;
  const dashboardStats = dashboardData?.stats || null;
  const weeklyActivity = dashboardData?.weeklyActivity || null;
  const submissionStats = dashboardData?.topicsByType || null;
  const courses = dashboardData?.courseProgress || [];

  // Merge achievements with defaults
  const achievements = React.useMemo(() => {
    const apiAchievements = dashboardData?.achievements || [];
    if (apiAchievements.length === 0) return DEFAULT_ACHIEVEMENTS;
    
    return DEFAULT_ACHIEVEMENTS.map(def => {
      const found = apiAchievements.find(a => a.id === def.id);
      return found ? { ...def, ...found } : def;
    });
  }, [dashboardData?.achievements]);

  // Calculate progress data from stats or fallback to course data
  const progressData = React.useMemo(() => {
    if (dashboardStats) {
      return {
        totalTopics: dashboardStats.totalTopics || 0,
        completedTopics: dashboardStats.totalTopicsCompleted || 0,
        overallProgress: dashboardStats.overallProgress || 0,
        courseProgress: (courses || []).map((cp, idx) => ({
          name: cp.courseName?.length > 20 ? cp.courseName.slice(0, 20) + "..." : cp.courseName,
          progress: cp.progress || 0,
          completed: cp.completedTopics || 0,
          total: cp.totalTopics || 0,
          color: GRADIENT_COLORS[idx % GRADIENT_COLORS.length],
          courseId: cp.courseId,
        })),
      };
    }

    // Fallback: use courseProgress directly from consolidated API
    const courseProgressList = (courses || []).map((cp, idx) => ({
      name: cp.courseName?.length > 20 ? cp.courseName.slice(0, 20) + "..." : cp.courseName,
      progress: cp.progress || 0,
      completed: cp.completedTopics || 0,
      total: cp.totalTopics || 0,
      color: GRADIENT_COLORS[idx % GRADIENT_COLORS.length],
      courseId: cp.courseId,
    }));

    const totalTopics = courseProgressList.reduce((sum, c) => sum + (c.total || 0), 0);
    const completedTopics = courseProgressList.reduce((sum, c) => sum + (c.completed || 0), 0);

    return {
      totalTopics,
      completedTopics,
      overallProgress: totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0,
      courseProgress: courseProgressList,
    };
  }, [dashboardStats, courses]);

  // Topic type distributions - now uses topicsByType from consolidated API
  // 1) Total topics by type
  const topicTotalsData = React.useMemo(() => {
    if (submissionStats) {
      const { theory, mcq, coding } = submissionStats;
      return [
        { name: "Theory", value: theory?.total || 0, color: CHART_COLORS.primary },
        { name: "MCQ", value: mcq?.total || 0, color: CHART_COLORS.secondary },
        { name: "Coding", value: coding?.total || 0, color: CHART_COLORS.success },
      ];
    }

    // Fallback: empty data
    return [
      { name: "Theory", value: 0, color: CHART_COLORS.primary },
      { name: "MCQ", value: 0, color: CHART_COLORS.secondary },
      { name: "Coding", value: 0, color: CHART_COLORS.success },
    ];
  }, [submissionStats]);

  // 2) Completed topics by type (used to show 50/50 style distribution)
  const topicCompletedData = React.useMemo(() => {
    if (submissionStats) {
      const { theory, mcq, coding } = submissionStats;
      return [
        { name: "Theory", value: theory?.completed || 0, color: CHART_COLORS.primary },
        { name: "MCQ", value: mcq?.completed || 0, color: CHART_COLORS.secondary },
        { name: "Coding", value: coding?.completed || 0, color: CHART_COLORS.success },
      ];
    }

    // Fallback: no completion tracking without backend support
    return [
      { name: "Theory", value: 0, color: CHART_COLORS.primary },
      { name: "MCQ", value: 0, color: CHART_COLORS.secondary },
      { name: "Coding", value: 0, color: CHART_COLORS.success },
    ];
  }, [submissionStats]);

  const totalTopicsByType = React.useMemo(
    () => topicTotalsData.reduce((sum, d) => sum + (Number(d?.value) || 0), 0),
    [topicTotalsData]
  );

  const completedTopicsByType = React.useMemo(
    () => topicCompletedData.reduce((sum, d) => sum + (Number(d?.value) || 0), 0),
    [topicCompletedData]
  );

  const formatActivityLabel = (day, date) => {
    if (!date) return day || "";
    const dt = new Date(date);
    if (Number.isNaN(dt.getTime())) return day || String(date);

    try {
      const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
      return formatter.format(dt);
    } catch {
      return dt.toLocaleDateString();
    }
  };

  // Weekly activity chart data (last 7 days)
  const activityChartData = React.useMemo(() => {
    const points =
      (Array.isArray(weeklyActivity?.last7Days) && weeklyActivity.last7Days) ||
      (Array.isArray(weeklyActivity?.weeklyActivity) && weeklyActivity.weeklyActivity) ||
      [];

    if (points.length > 0) {
      return points.map((d) => ({
        name: formatActivityLabel(d.day, d.date),
        topics: d.topicsCompleted || 0,
        minutes: d.minutesSpent || 0,
      }));
    }

    // Fallback: last 7 days labels (empty)
    return Array.from({ length: 7 }).map((_, i) => ({
      name: `Day ${i + 1}`,
      topics: 0,
      minutes: 0,
    }));
  }, [weeklyActivity]);

  // Stats from API or defaults
  const stats = {
    coursesEnrolled: dashboardStats?.totalCoursesEnrolled || courses.length,
    topicsCompleted: progressData.completedTopics,
    currentStreak: dashboardStats?.currentStreak || 0,
    totalXp: dashboardStats?.totalXp || 0,
  };

  const getInitials = () => {
    const { firstName, lastName } = userDetails || {};
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "U";
  };

  if (loading) {
    return <SkeletonDashboard />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-red-500 mb-4"></i>
          <p className="text-gray-600 font-medium">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <SeoMeta
        title="My Dashboard | Code Arena"
        description="Track your learning progress, achievements, and analytics"
        url="https://www.code-arena.in/dashboard"
      />

      {/* Hero Section */}
      <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient.replace('from-', 'from-slate-900 via-').split('via-')[0]}via-slate-800 to-slate-900 text-white overflow-hidden`}>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-theme-primary/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-theme-secondary/20 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative ca-container py-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="relative"
            >
              <div className={`w-28 h-28 rounded-2xl bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center text-4xl font-bold shadow-2xl ring-4 ring-white/20`}>
                {getInitials()}
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
            </motion.div>

            <div className="text-center md:text-left flex-1">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl md:text-4xl font-bold mb-2"
              >
                Welcome back, {userDetails?.firstName || "Learner"}! 👋
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-slate-300 text-lg"
              >
                {userDetails?.email}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-wrap justify-center md:justify-start gap-4 mt-4"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm">
                  <i className="ri-fire-line text-orange-400"></i>
                  {stats.currentStreak} Day Streak
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm">
                  <i className="ri-trophy-line text-yellow-400"></i>
                  {stats.topicsCompleted} Topics Done
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm">
                  <i className="ri-star-line text-purple-400"></i>
                  {stats.totalXp} XP
                </span>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="hidden lg:block"
            >
              <ProgressRing
                progress={progressData.overallProgress}
                size={140}
                strokeWidth={12}
                color="#3B82F6"
                label="Complete"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="ca-container py-8 space-y-8"
      >
        {/* Quick Stats */}
        <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { label: "Courses Enrolled", value: stats.coursesEnrolled, icon: "ri-book-open-line", bgColor: "bg-blue-100", iconColor: "text-blue-600" },
            { label: "Topics Completed", value: stats.topicsCompleted, icon: "ri-check-double-line", bgColor: "bg-green-100", iconColor: "text-green-600" },
            { label: "Current Streak", value: `${stats.currentStreak} days`, icon: "ri-fire-line", bgColor: "bg-orange-100", iconColor: "text-orange-600" },
            { label: "Total XP", value: stats.totalXp, icon: "ri-star-line", bgColor: "bg-purple-100", iconColor: "text-purple-600" },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              variants={cardHover}
              initial="rest"
              whileHover="hover"
              className="bg-white/80 backdrop-blur-xl border border-gray-200/60 rounded-2xl p-5 shadow-lg cursor-pointer"
            >
              <div className={`w-10 h-10 rounded-xl ${stat.bgColor} flex items-center justify-center mb-3`}>
                <i className={`${stat.icon} text-xl ${stat.iconColor}`}></i>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Charts Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ChartCard title="Learning Progress" subtitle="Your course completion status">
              <div className="space-y-4">
                {progressData.courseProgress.length > 0 ? (
                  progressData.courseProgress.map((course, idx) => (
                    <div key={idx} className="group">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{course.name}</span>
                        <span className="text-sm text-gray-500">{course.completed}/{course.total}</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${course.progress}%` }}
                          transition={{ duration: 1, delay: idx * 0.2 }}
                          className={`h-full rounded-full bg-gradient-to-r ${currentTheme.colors.gradient}`}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <i className="ri-book-open-line text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">No courses enrolled yet</p>
                    <Link to="/courses" className={`inline-block mt-4 px-4 py-2 bg-gradient-to-r ${currentTheme.colors.gradient} text-white text-sm rounded-lg hover:opacity-90`}>
                      Browse Courses
                    </Link>
                  </div>
                )}
              </div>
            </ChartCard>
          </div>

          <div className="space-y-6">
            <ChartCard title="Topic Types" subtitle="Total topics distribution">
              <DonutChart
                data={topicTotalsData}
                height={200}
                centerLabel="Total"
                centerValue={totalTopicsByType}
              />
            </ChartCard>

            <ChartCard title="Topic Completion" subtitle="Completed topics distribution">
              <DonutChart
                data={topicCompletedData}
                height={200}
                centerLabel="Completed"
                centerValue={completedTopicsByType}
              />
            </ChartCard>
          </div>
        </motion.div>

        {/* Learning Activity & Achievements */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Weekly Activity"
            subtitle={`${
              weeklyActivity?.totalLast7Days?.topicsCompleted ||
              weeklyActivity?.totalThisWeek?.topicsCompleted ||
              0
            } topics in last 7 days`}
          >
            <SimpleAreaChart 
              data={activityChartData} 
              dataKey="topics" 
              height={220}
              color={CHART_COLORS.primary}
            />
          </ChartCard>

          <ChartCard title="Achievements" subtitle="Your earned badges">
            <div className="grid grid-cols-3 gap-4">
              {achievements.map((badge, idx) => (
                <motion.div
                  key={badge.id || idx}
                  whileHover={{ scale: badge.unlocked ? 1.05 : 1 }}
                  className={`relative p-4 rounded-xl text-center ${
                    badge.unlocked
                      ? "bg-gradient-to-br " + badge.color + " text-white shadow-lg"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  <i className={`${badge.icon} text-2xl mb-2 block`}></i>
                  <p className="text-xs font-medium">{badge.label}</p>
                  {!badge.unlocked && (
                    <div className="absolute inset-0 bg-gray-200/50 rounded-xl flex items-center justify-center">
                      <i className="ri-lock-line text-gray-400"></i>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </ChartCard>
        </motion.div>

        {/* Continue Learning */}
        <motion.div variants={itemVariants}>
          <ChartCard
            title="Continue Learning"
            subtitle="Pick up where you left off"
            action={
              <Link to="/courses" className="text-sm text-theme-primary hover:opacity-80 font-medium">
                View All →
              </Link>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(progressData.courseProgress.length > 0 ? progressData.courseProgress : courses).slice(0, 3).map((item, idx) => {
                const course = item.courseId ? courses.find(c => c.id === item.courseId) || item : item;
                const courseId = item.courseId || course.id;
                const percent = item.progress || 0;
                const completed = item.completed || 0;
                const total = item.total || course.chapters?.reduce((sum, ch) => sum + (ch.topics?.length || 0), 0) || 0;
                const courseName = item.name || course.title;

                return (
                  <motion.div
                    key={courseId || idx}
                    whileHover={{ y: -4 }}
                    className="group bg-gradient-to-br from-gray-50 to-white border border-gray-200/60 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center text-white text-lg font-bold flex-shrink-0`}>
                        {courseName?.[0] || "C"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                          {courseName}
                        </h4>
                        <p className="text-sm text-gray-500">{course.chapters?.length || 0} chapters</p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{percent}% complete</span>
                        <span>{completed}/{total}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${currentTheme.colors.gradient} rounded-full transition-all duration-500`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    <Link
                      to={`/courses/${courseId}`}
                      className="mt-4 block text-center py-2 text-sm font-medium text-theme-primary hover:bg-gray-50 dark:hover:bg-white/5 rounded-lg transition-colors"
                    >
                      Continue →
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </ChartCard>
        </motion.div>
      </motion.div>
    </div>
  );
}
